import { Column, Header } from '@/components/SettingsLayout';
import styles from './styles.module.css';
import { useGetUser } from '@/hooks';
import { IconType, Text } from '@/ui';
import { useTranslation } from 'react-i18next';
import DashboardSkeleton from './DashboardSkeleton';
import { useGetOrdersQuery } from '@/store/slices/ordersSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import GeneralCustomersCard from '@/components/CustomerCard/CustomerCard';
import TopOrganizations from './components/TopOrganizations';
import { CompanyTypes } from '@/types';
import { formatCurrency } from '@/utils/currency';
import {
  useGetDashboardMetricsQuery,
  useGetTopCompaniesQuery,
  useGetTopOrganizationsQuery,
} from '@/store';
import { TimePeriod, TopMetricParams, OrderMetricBy } from '@/services/Dashboard/dashboard.service';
import useFormatNumber from '@/hooks/useFormatNumber';
import { useState } from 'react';
import { CurrencyCode } from '@final-commerce/common';

const Dashboard = () => {
  const { t } = useTranslation();
  const { company, currentUser } = useGetUser();
  const formatNumber = useFormatNumber();
  const [companyFilter, setCompanyFilter] = useState<TopMetricParams>({
    timePeriod: TimePeriod.ALL_TIME,
    sortBy: OrderMetricBy.TOTAL_PAYMENT_VOLUME,
  });
  const [orgFilter, setOrgFilter] = useState<TopMetricParams>({
    timePeriod: TimePeriod.ALL_TIME,
    sortBy: OrderMetricBy.TOTAL_PAYMENT_VOLUME,
  });
  const [activeTimePeriod, setActiveTimePeriod] = useState<TimePeriod>(TimePeriod.ALL_TIME);
  const metricQuery = {
    companyId: currentUser?.companyId,
    timePeriod: activeTimePeriod,
    organizationId: currentUser.companyId,
  };

  const { data: metrics } = useGetDashboardMetricsQuery(
    currentUser?.companyId && metricQuery ? metricQuery : skipToken,
  );

  const { data: topOrganizationData } = useGetTopOrganizationsQuery(
    currentUser?.companyId ? { id: currentUser?.companyId, filter: orgFilter } : skipToken,
  );

  const { data: top_companies } = useGetTopCompaniesQuery(
    currentUser?.companyId
      ? {
          id: currentUser?.companyId,
          organizationId: currentUser.companyId,
          filter: companyFilter,
        }
      : skipToken,
  );
  const { isLoading: loadingOrders } = useGetOrdersQuery(
    currentUser?.companyId
      ? {
          companyId: currentUser.companyId,
          limit: 7,
        }
      : skipToken,
  );

  const loading = loadingOrders;

  if (loading) {
    return <DashboardSkeleton />;
  }

  const handleCompanyFilterChange = (key: keyof TopMetricParams, value: any) => {
    setCompanyFilter({ ...companyFilter, [key]: value });
    if (key === 'timePeriod') setActiveTimePeriod(value);
  };

  const handleOrgFilterChange = (key: keyof TopMetricParams, value: any) => {
    setOrgFilter({ ...orgFilter, [key]: value });
    if (key === 'timePeriod') setActiveTimePeriod(value);
  };

  return (
    <Column gapSize='24'>
      <Header>
        <Column gapSize='12'>
          <Text size='xl' weight='medium'>
            {t('Dashboard.title', {
              name: currentUser.firstName,
            })}
          </Text>
          <Text size='m' weight='light-medium'>
            {t('Dashboard.title_comment')}
          </Text>
        </Column>
      </Header>
      <div className={`${styles.cardGrid}`}>
        {company?.type === CompanyTypes.ROOT && (
          <div className={styles.blueSvg}>
            <GeneralCustomersCard
              testId='total-organizations-data'
              title={t('Dashboard.total_organizations')}
              data={`${metrics?.numberOfOrganizations ?? 0} `}
              icon={{
                type: IconType.Building,
                width: 56,
              }}
            />
          </div>
        )}
        <div className={styles.purpleSvg}>
          <GeneralCustomersCard
            testId='total-companies-data'
            title={t('Dashboard.total_companies')}
            data={`${metrics?.numberOfCompanies ?? 0}`}
            icon={{
              type: IconType.Bag,
              width: 56,
            }}
          />
        </div>
        <div className={styles.orangeSvg}>
          <GeneralCustomersCard
            testId='transaction-count-data'
            title={t('Dashboard.transaction_count')}
            // comment={t('Dashboard.total_payment_volume_comment')}
            data={formatNumber(metrics?.totalTransaction ?? 0, undefined, 0)}
            icon={{
              type: IconType.ExchangeDollar,
              width: 56,
            }}
          />
        </div>
        <div className={styles.tealSvg}>
          <GeneralCustomersCard
            testId='total-payment-volume-data'
            title={t('Dashboard.total_payment_volume')}
            // comment={t('Dashboard.total_payment_volume_comment')}
            data={
              metrics?.currency
                ? formatCurrency(metrics.totalPaymentVolume ?? 0, metrics.currency as CurrencyCode)
                : String(metrics?.totalPaymentVolume ?? 0)
            }
            icon={{
              type: IconType.Coin,
              width: 56,
            }}
          />
        </div>
      </div>
      {company?.type === CompanyTypes.ROOT && (
        <TopOrganizations<TopMetricParams>
          title={'Dashboard.top_organizations'}
          data={topOrganizationData}
          dateRangeKey={'timePeriod'}
          sortKey={'sortBy'}
          onFilterChange={handleOrgFilterChange}
          entityType={CompanyTypes.ORGANIZATION}
        />
      )}
      <TopOrganizations<TopMetricParams>
        title={'Dashboard.top_companies'}
        data={top_companies}
        dateRangeKey={'timePeriod'}
        sortKey={'sortBy'}
        onFilterChange={handleCompanyFilterChange}
        entityType={CompanyTypes.COMPANY}
      />
    </Column>
  );
};

export default Dashboard;
